import React, { createContext, useContext, useState, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import axios from 'axios';
import { format } from 'date-fns';
import env from 'config/env';

export interface HabitFrequency {
  type: 'daily' | 'weekly' | 'monthly';
  days?: number[]; // For weekly habits: 0-6 (Sunday-Saturday)
  dates?: number[]; // For monthly habits: 1-31
}

export interface Habit {
  id: string;
  name: string;
  description?: string;
  frequency: HabitFrequency;
  color?: string;
  icon?: string;
  created_at: string;
  reminder_time?: string;
  is_active: boolean;
}

export interface HabitProgress {
  id: string;
  habit_id: string;
  date: string;
  completed: boolean;
  notes?: string;
}

interface HabitContextType {
  habits: Habit[];
  progress: { [key: string]: HabitProgress[] };
  isLoading: boolean;
  createHabit: (habit: Omit<Habit, 'id' | 'created_at'>) => Promise<void>;
  updateHabit: (id: string, habit: Partial<Habit>) => Promise<void>;
  deleteHabit: (id: string) => Promise<void>;
  toggleHabitCompletion: (habitId: string, date: Date, notes?: string) => Promise<void>;
  fetchHabits: () => Promise<void>;
  fetchProgress: (startDate: Date, endDate: Date) => Promise<void>;
}

const HabitContext = createContext<HabitContextType | undefined>(undefined);

export function HabitProvider({ children }: { children: React.ReactNode }) {
  const [habits, setHabits] = useState<Habit[]>([]);
  const [progress, setProgress] = useState<{ [key: string]: HabitProgress[] }>({});
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const fetchHabits = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${env.API_URL}/habits`);
      setHabits(response.data);
    } catch (error) {
      toast({
        title: 'Error fetching habits',
        description: 'Could not fetch habits',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const fetchProgress = useCallback(async (startDate: Date, endDate: Date) => {
    try {
      const response = await axios.get(`${env.API_URL}/habits/progress`, {
        params: {
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd'),
        },
      });

      const progressByHabit: { [key: string]: HabitProgress[] } = {};
      response.data.forEach((progress: HabitProgress) => {
        if (!progressByHabit[progress.habit_id]) {
          progressByHabit[progress.habit_id] = [];
        }
        progressByHabit[progress.habit_id].push(progress);
      });

      setProgress(progressByHabit);
    } catch (error) {
      toast({
        title: 'Error fetching progress',
        description: 'Could not fetch habit progress',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [toast]);

  const createHabit = async (habit: Omit<Habit, 'id' | 'created_at'>) => {
    try {
      const response = await axios.post(`${env.API_URL}/habits`, habit);
      setHabits([...habits, response.data]);
      toast({
        title: 'Habit created',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Error creating habit',
        description: 'Could not create habit',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const updateHabit = async (id: string, habitUpdate: Partial<Habit>) => {
    try {
      const response = await axios.put(`${env.API_URL}/habits/${id}`, habitUpdate);
      setHabits(habits.map(h => h.id === id ? response.data : h));
      toast({
        title: 'Habit updated',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Error updating habit',
        description: 'Could not update habit',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const deleteHabit = async (id: string) => {
    try {
      await axios.delete(`${env.API_URL}/habits/${id}`);
      setHabits(habits.filter(h => h.id !== id));
      toast({
        title: 'Habit deleted',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Error deleting habit',
        description: 'Could not delete habit',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const toggleHabitCompletion = async (habitId: string, date: Date, notes?: string) => {
    const formattedDate = format(date, 'yyyy-MM-dd');
    const habitProgress = progress[habitId]?.find(p => p.date === formattedDate);
    
    try {
      if (habitProgress) {
        // Toggle existing progress
        const response = await axios.put(`${env.API_URL}/habits/progress/${habitProgress.id}`, {
          completed: !habitProgress.completed,
          notes,
        });
        
        setProgress({
          ...progress,
          [habitId]: progress[habitId].map(p =>
            p.id === habitProgress.id ? response.data : p
          ),
        });
      } else {
        // Create new progress
        const response = await axios.post(`${env.API_URL}/habits/progress`, {
          habit_id: habitId,
          date: formattedDate,
          completed: true,
          notes,
        });
        
        setProgress({
          ...progress,
          [habitId]: [...(progress[habitId] || []), response.data],
        });
      }
    } catch (error) {
      toast({
        title: 'Error updating progress',
        description: 'Could not update habit progress',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <HabitContext.Provider
      value={{
        habits,
        progress,
        isLoading,
        createHabit,
        updateHabit,
        deleteHabit,
        toggleHabitCompletion,
        fetchHabits,
        fetchProgress,
      }}
    >
      {children}
    </HabitContext.Provider>
  );
}

export function useHabits() {
  const context = useContext(HabitContext);
  if (context === undefined) {
    throw new Error('useHabits must be used within a HabitProvider');
  }
  return context;
} 